import React, { createContext, useState, useEffect, useContext } from "react";
import { permissions } from "../permissions";
import TokenExpiryPopup from "../components/TokenExpiryPopup";
import { USER_DATA } from "../config/variables";
import { GetMyPermissions } from "../api/users/getMyPermissions";
import { GetCurrentUser } from "../api/users/getCurrentUser";
import { MODULE_PERMISSIONS } from "../contstants";

const UserContext = createContext(null);

export const UserProvider = ({ children }) => {
  const [userData, setUserData] = useState(null);
  const [dynamicPermissions, setDynamicPermissions] = useState(null);
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);
  // True when the IIP surface answered users/me; the roles UI hides otherwise.
  const [iipAvailable, setIipAvailable] = useState(false);

  useEffect(() => {
    const userDataString = localStorage.getItem(USER_DATA);
    if (!userDataString) return undefined;

    // Paint from cache, then revalidate: the cache is only written at login, so
    // without this a role change wouldn't show until the next login.
    setUserData(JSON.parse(userDataString));

    let cancelled = false;
    GetCurrentUser()
      .then((res) => {
        const fresh = res?.data;
        if (!cancelled && fresh) {
          setUserData(fresh);
          localStorage.setItem(USER_DATA, JSON.stringify(fresh));
        }
      })
      .catch(() => {
        // Keep the cached profile if revalidation fails.
      });

    return () => {
      cancelled = true;
    };
  }, []);

  // Load the dynamic permission map once the user is known. A non-migrated user
  // (no IIP profile) leaves it null, so dynamic gates stay off.
  useEffect(() => {
    if (!userData?.id) {
      setDynamicPermissions(null);
      setPermissionsLoaded(false);
      setIipAvailable(false);
      return;
    }

    let cancelled = false;
    const loadPermissions = async () => {
      try {
        const me = await GetMyPermissions();
        // The dynamic map governs only users on a dynamic role; legacy users
        // keep null so static gating runs regardless of the IIP toggle.
        const hasDynamicRole = Array.isArray(me?.roles) && me.roles.length > 0;
        if (!cancelled) {
          setDynamicPermissions(hasDynamicRole ? me?.permissions || {} : null);
          setIipAvailable(true);
        }
      } catch (err) {
        // IIP disabled or fetch failure: no dynamic gating, no roles UI.
        if (!cancelled) {
          setDynamicPermissions(null);
          setIipAvailable(false);
        }
      } finally {
        if (!cancelled) setPermissionsLoaded(true);
      }
    };
    loadPermissions();

    return () => {
      cancelled = true;
    };
  }, [userData?.id]);

  const can = (feature, action) => {
    if (!userData || !userData?.role) return false;

    const rolePermissions = permissions[userData?.role] || {};

    if (
      rolePermissions[feature] &&
      rolePermissions[feature][action] !== undefined
    ) {
      return rolePermissions[feature][action];
    }

    return false;
  };

  // dynamicPermissions is keyed "category.feature" -> { read, write, delete },
  // and is null for non-migrated users.
  const hasDynamicPermissions = () => !!dynamicPermissions;

  const hasPermission = (resource, access = "read") => {
    if (!dynamicPermissions) return false;
    return dynamicPermissions?.[resource]?.[access] === true;
  };

  // Dynamic module visibility. A migrated user (carrying a permission map) can
  // see a module only if their role grants the mapped permission. Users with no
  // map (non-migrated) and modules with no mapping (e.g. audit_logs) are
  // unaffected, so this is additive to the existing gates.
  const canAccessModule = (moduleKey) => {
    if (!dynamicPermissions) return true;
    const mapping = MODULE_PERMISSIONS[moduleKey];
    if (!mapping) return true;
    return dynamicPermissions?.[mapping.resource]?.[mapping.access] === true;
  };

  return (
    <UserContext.Provider
      value={{
        userData,
        setUserData,
        can,
        hasPermission,
        hasDynamicPermissions,
        canAccessModule,
        permissionsLoaded,
        iipAvailable,
      }}
    >
      {children}
      {userData && <TokenExpiryPopup />}
    </UserContext.Provider>
  );
};

export const useUserContext = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUserContext must be used within a UserProvider");
  }
  return context;
};
